import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { useFormState } from "./login/useFormState";
import { useAuthState } from "./login/useAuthState";
import { useDomains } from "./login/useDomains";
import { useAuthFlows } from "./login/useAuthFlows";
import { useUrlParams } from "./login/useUrlParams";

export function useLoginForm() {
  const router = useRouter();
  const { data: session, status } = useSession();

  // Hooks especializados
  const formState = useFormState();
  const authState = useAuthState();
  const domains = useDomains();
  const authFlows = useAuthFlows();
  const urlParams = useUrlParams();

  const { auth, setLoading, setError, clearErrors } = authState;
  const { error, setError: setFormError, clearError } = formState;
  const {
    checkAuthParams,
    markCodeAsProcessed,
    isCodeProcessed,
    setProcessing,
    isProcessingState,
    removeCode,
  } = urlParams;

  // Verificar parâmetros de autenticação na URL
  const checkAndProcessAuthParams = async () => {
    const { ta, h, code } = checkAuthParams();

    // Primeiro verifica parâmetros do SSO GSI (TA/H) - prioridade
    if (ta && h) {
      console.log("🔍 Detectados parâmetros SSO TA/H:", { ta, h });
      await handleSSO(ta, h);
      return true;
    }

    // Depois verifica parâmetro do GovBR (code)
    if (code && !isCodeProcessed(code)) {
      console.log("🔍 Detectado parâmetro code do GovBR");
      await handleGovBRSSO(code);
      return true;
    }

    return false;
  };

  // Handler para SSO TA/H
  const handleSSO = async (ta: string, h: string) => {
    try {
      setLoading("sso", true);
      clearErrors();
      clearError();
      await authFlows.processSSO(ta, h);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Erro desconhecido no SSO";
      setError("sso", errorMessage);
      setFormError(errorMessage);
      window.history.replaceState({}, "", "/");
    }
  };

  // Handler para GovBR via Code
  const handleGovBRSSO = async (code: string) => {
    // Prevenir processamento duplicado
    if (isCodeProcessed(code) || isProcessingState()) {
      console.log(
        "🔄 Código GovBR já processado ou processamento em andamento, ignorando..."
      );
      return;
    }

    try {
      setProcessing(true);
      markCodeAsProcessed(code);
      setLoading("govbr", true);
      clearErrors();
      clearError();

      await authFlows.processGovBRSSO(code);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Erro desconhecido no GovBR";
      setError("govbr", errorMessage);
      setFormError(errorMessage);
      removeCode(code); // Remove o código do conjunto em caso de erro para permitir nova tentativa
    } finally {
      setProcessing(false);
    }
  };

  // Handler para GovBR (início do fluxo)
  const handleGovBrLogin = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setLoading("govbr", true);
    clearErrors();
    clearError();

    try {
      authFlows.startGovBRFlow();
    } catch (error) {
      const errorMessage = "Erro ao iniciar autenticação GovBR";
      setError("govbr", errorMessage);
      setFormError(errorMessage);
    }
  };

  // Handler para GSI tradicional
  const handleGSI = async () => {
    clearErrors();
    clearError();
    setLoading("gsi", true);

    try {
      await authFlows.processGSI(
        formState.selectedDomain,
        formState.email,
        formState.password
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Erro desconhecido";
      setError("gsi", errorMessage);
      setFormError(errorMessage);
    }
  };

  // Efeito principal
  useEffect(() => {
    // Redirecionar se já estiver autenticado
    if (status === "authenticated") {
      router.push("/dashboard");
      return;
    }

    const initialize = async () => {
      try {
        // Verifica se há parâmetros de autenticação na URL
        const hasAuthParams = await checkAndProcessAuthParams();

        // Busca domínios (só se não tiver parâmetros de auth)
        if (!hasAuthParams) {
          await domains.refetch();
        }
      } catch (error) {
        console.error("❌ Erro na inicialização:", error);
      }
    };

    initialize();
  }, [status, router]);

  // Calcular erro global
  const globalError =
    error || auth.gsi.error || auth.govbr.error || auth.sso.error;

  return {
    // Estado do formulário
    email: formState.email,
    password: formState.password,
    showPassword: formState.showPassword,
    selectedDomain: formState.selectedDomain,

    // Estado de autenticação
    auth,

    // Domínios
    domains: domains.domains,
    loadingDomains: domains.loading,

    // Erros
    error: globalError,

    // Handlers
    handleChange: formState.handleChange,
    handleGSI,
    handleGovBrLogin,
    togglePasswordVisibility: formState.togglePasswordVisibility,
  };
}
